/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/**
 * @class FooterMessageBox
 * @hideconstructor
 * @classdesc View class of the FooterMessageBox
 */
define([
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'react-dom',
	'./FooterMessageBoxAPI',
	'../../../lib/@waca/dashboard-common/dist/api/PropertiesProviderAPI',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../app/nls/StringResources'
], (React, Carbon_toolkit, ReactDOM, FooterMessageBoxAPI, PropertiesProviderAPI, APIFactory, StringResources) => {
	const { FlexLayout } = Carbon_toolkit;
	const PROPERTY_ID = 'showFooter';
	class FooterMessageBox {
		constructor(options) {
			this._canvas = options.features['Canvas'];
			this._api = APIFactory.createAPI(this, [FooterMessageBoxAPI, PropertiesProviderAPI]);
			options.features['Properties'].registerProvider(this._api);
			this._canvas.on('change:property:showFooter', this._onShowFooterPropertyChange, this);
		}

		getPropertyLayoutList() {
			return [{
				id: PROPERTY_ID,
				type: 'Section',
				collapsible: false,
				label: StringResources.get('showFooterSectionText'),
				position: 2.5
			}];
		}

		getPropertyList() {
			const parentSectionId = 'general.canvas';
			return [{
				id: PROPERTY_ID,
				supportsUndoRedo: true,
				defaultValue: true,
				editor: {
					sectionId: parentSectionId + '.' + PROPERTY_ID,
					position: 1,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('showFooterToggleButtonText')
					}
				}
			}];
		}

		_createContainerIfNeeded() {
			if (!this.el) {
				const canvasContentViewDOM = this._canvas.getContent().getFeature('ContentViewDOM');
				if(!canvasContentViewDOM && !this._canvasContentReady) {
					this._canvas.getContent().getFeature('state').whenStatusChanges('rendered').then(() => {
						this._canvasContentReady = true;
						this.setMessage(this._messageContentText, this._messageBoldText);
					});
				} else {
					const node = canvasContentViewDOM.getNode();
					if (node) {
						const footerContainerNode = node.closest('.dashboardFrameCentre');
						if (footerContainerNode) {
							this.el = document.createElement('div');
							this.el.className = 'FooterMessageBoxContainer';
							footerContainerNode.append(this.el);
						}
					}
				}
			}
		}

		reset() { }

		_onShowFooterPropertyChange(/*payload*/) {
			this.setMessage(this._messageContentText, this._messageBoldText);
		}

		setMessage(messageContentText, messageBoldText) {
			// cache the messages for toggling properties event
			this._messageContentText = messageContentText;
			this._messageBoldText = messageBoldText;
			if (this._canvas.getPropertyValue('showFooter') !== false && messageContentText) {
				this.render(messageContentText, messageBoldText);
			} else {
				this.remove();
			}
		}

		render(messageContentText, messageBoldText) {
			this._createContainerIfNeeded();
			if (messageContentText && this.el) {
				const component = this._renderComponent(messageContentText, messageBoldText);
				ReactDOM.render(component, this.el);
			}
		}

		_renderComponent(messageContentText, messageBoldText) {
			const PERIOD = '.';
			return (<FlexLayout className='messageBoxContainer responsiveColor' justifyContent={'flex-end'}>
				{messageContentText}&nbsp;
				<span className='messageBoxBoldContent'>{messageBoldText}</span>
				{PERIOD}
			</FlexLayout>);
		}

		getAPI() {
			return this._api;
		}

		remove() {
			if (this.el) {
				ReactDOM.unmountComponentAtNode(this.el);
			}
		}

		destroy() {
			this.remove();
			this.el = null;
			this._canvas.off('change:property:showFooter', this._onShowFooterPropertyChange, this);
			this._canvas = null;
		}
	}

	return FooterMessageBox;
});
